import { lazy, Suspense } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router";
import { queryClient } from "@/services/queryClient";
import { SnackbarProvider } from "@/contexts/SnackbarContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuthSession } from "@/hooks/useAuthSession";
import { router } from "./routes";

// Dev-only, dynamically imported so `import.meta.env.DEV` (statically false
// in production) lets the bundler drop this branch - and the devtools
// package with it - from the production build entirely.
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

function App() {
  const { authLoading, isTokenReady, user } = useAuthSession();

  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider>
        <ThemeProvider>
          <SidebarProvider>
            <TooltipProvider>
              {authLoading || (user && !isTokenReady) ? (
                <div className="flex h-screen items-center justify-center">Loading...</div>
              ) : (
                <RouterProvider router={router} />
              )}
            </TooltipProvider>
          </SidebarProvider>
        </ThemeProvider>
      </SnackbarProvider>
      {ReactQueryDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}

export default App;
